import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../../utils/axios';
import { formatQuantity, getText } from '../../shared/TaxInvoice/taxInvoiceUtils';

const DeliveryNotePreview = ({ data, isLoading }) => {
    const [searchParams] = useSearchParams();
    const { id: routeId } = useParams();
    const preview = searchParams.get('preview');
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);

    const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = MONTH_NAMES[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const hasContactValue = (value) => {
        const text = getText(value).trim();
        return Boolean(text) && text !== '-';
    };

    const getItemDisplay = (row) =>
        getText(row.item?.label)
        || getText(row.item?.item_code)
        || getText(row.item_description)
        || '-';

    const getGroupedItems = () => {
        if (Array.isArray(data?.grouped_items) && data.grouped_items.length > 0) {
            return data.grouped_items;
        }

        const lineItems = Array.isArray(data?.items)
            ? data.items
            : (Array.isArray(data?.delivery_note_items) ? data.delivery_note_items : []);

        const groupedMap = new Map();
        lineItems.forEach((item) => {
            const key = item?.job_order_id || item?.job_order_number || 'unknown';
            if (!groupedMap.has(key)) {
                groupedMap.set(key, {
                    job_order_id: item?.job_order_id || null,
                    job_order_number: getText(item?.job_order_number) || '-',
                    employee_name: getText(item?.employee_name) || '-',
                    items: [],
                });
            }
            groupedMap.get(key).items.push(item);
        });

        return Array.from(groupedMap.values()).map((group, index) => ({
            ...group,
            serial_number: index + 1,
        }));
    };

    useEffect(() => {
        if (!data) return;
        if (preview) setShowPrintModal(true);
    }, [preview, data]);

    useEffect(() => {
        if (!showPrintModal) {
            setIsPrinting(false);
            return undefined;
        }

        const cssHref = `${process.env.PUBLIC_URL || ''}/css/delivery_print.css`;
        const existing = document.getElementById('delivery-note-preview-css');
        if (existing) existing.remove();

        const link = document.createElement('link');
        link.id = 'delivery-note-preview-css';
        link.rel = 'stylesheet';
        link.href = cssHref;
        document.head.appendChild(link);

        return () => {
            document.getElementById('delivery-note-preview-css')?.remove();
        };
    }, [showPrintModal]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center p-4">
                <h5>No data available</h5>
            </div>
        );
    }

    const handlePrint = () => setShowPrintModal(true);
    const handleClosePrintModal = () => setShowPrintModal(false);

    const handlePdfPrint = async () => {
        const documentId = data?.id || routeId;
        if (!documentId) {
            toast.error('Delivery note ID is missing.');
            return;
        }

        setIsPrinting(true);
        try {
            const response = await axiosInstance.get(`/delivery_note_print/${encodeURIComponent(documentId)}`, {
                responseType: 'blob',
                headers: { Accept: 'application/pdf' },
            });

            const contentType = response.headers['content-type'] || '';
            if (!contentType.includes('pdf')) {
                const text = await response.data.text();
                let message = 'Failed to generate delivery note PDF.';
                try {
                    const parsed = JSON.parse(text);
                    message = parsed.error || parsed.message || message;
                } catch {
                    if (text) message = text;
                }
                toast.error(message);
                return;
            }

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            const opened = window.open(blobUrl, '_blank');
            if (!opened) {
                toast.error('Popup blocked. Please allow popups for this site.');
            }
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (error) {
            let message = 'Failed to load delivery note PDF.';
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const parsed = JSON.parse(text);
                    message = parsed.error || parsed.message || message;
                } catch {
                    // keep default message
                }
            } else {
                message = error.response?.data?.message || error.message || message;
            }
            toast.error(message);
            console.error('Failed to load PDF:', error);
        } finally {
            setIsPrinting(false);
        }
    };

    const vatNumber = getText(data.vat_number) || getText(data.customer?.vat_number);
    const phone = getText(data.phone_number) || getText(data.customer?.phone);

    return (
        <div className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Delivery Note Details</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <button className="btn btn-primary btn-sm no-print" onClick={handlePrint} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <i className="fas fa-print"></i>
                        Print Preview
                    </button>
                </div>
            </div>

            <div className="table-responsive mt-2">
                <table className="table table-sm table-borderless" style={{ marginBottom: 0 }}>
                    <tbody>
                        <tr>
                            <td>Delivery No:</td>
                            <td>{getText(data.invoice_number) || getText(data.number) || '-'}</td>
                            <td>Delivery Date:</td>
                            <td>{formatDate(data.invoice_date || data.delivery_date)}</td>
                        </tr>
                        <tr>
                            <td>Customer:</td>
                            <td>{getText(data.customer_name) || getText(data.customer?.label) || '-'}</td>
                            <td>Customer Code:</td>
                            <td>{getText(data.customer_code) || getText(data.customer?.code) || '-'}</td>
                        </tr>
                        <tr>
                            <td>Phone:</td>
                            <td>{phone || '-'}</td>
                        </tr>
                        <tr>
                            <td>Address:</td>
                            <td colSpan="3" style={{ whiteSpace: 'pre-line' }}>
                                BUILDING {getText(data.building) || '-'}, FLAT {getText(data.flat) || '-'}, ROAD {getText(data.road) || '-'}{'\n'}
                                BLOCK {getText(data.block) || '-'}, AREA {getText(data.area) || '-'}{'\n'}
                                KINGDOM OF BAHRAIN
                            </td>
                        </tr>
                        <tr>
                            <td>LPO No:</td>
                            <td>{getText(data.lpo_number?.label) || getText(data.lpo_number) || '-'}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="table-responsive mt-4">
                {(() => {
                    const groupedItems = getGroupedItems();
                    return (
                        <table className="table table-bordered" style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th className="text-nowrap p-3" style={{ width: '5%', textAlign: 'left' }}>No.</th>
                                    <th className="p-3" style={{ width: '18%', textAlign: 'left' }}>Name &amp; Staff No.</th>
                                    <th className="p-3" style={{ width: '45%', textAlign: 'left' }}>Description</th>
                                    <th className="text-nowrap p-3" style={{ width: '10%', textAlign: 'right' }}>Qty.</th>
                                    <th className="text-nowrap p-3" style={{ width: '8%', textAlign: 'left' }}>Unit</th>
                                    <th className="text-nowrap p-3" style={{ width: '14%', textAlign: 'left' }}>Job No.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedItems && groupedItems.length > 0 ? (
                                    groupedItems.flatMap((group, groupIndex) => (
                                        (Array.isArray(group.items) ? group.items : []).map((row, rowIndex) => {
                                            const unit = getText(row.unit) || getText(row.item?.unit) || '-';
                                            const qty = formatQuantity(row.quantity || row.qty || 0);
                                            const employeeName = getText(group.employee_name) || '-';
                                            const jobOrderNumber = getText(group.job_order_number) || getText(row.job_order_number) || '-';

                                            return (
                                                <tr key={`${group.job_order_id || group.job_order_number || groupIndex}-${row.id || rowIndex}`}>
                                                    <td className="text-nowrap p-3" style={{ textAlign: 'left' }}>
                                                        {rowIndex === 0 ? (group.serial_number || groupIndex + 1) : ''}
                                                    </td>
                                                    <td className="p-3 text-nowrap" style={{ textAlign: 'left' }}>
                                                        {rowIndex === 0 ? employeeName : ''}
                                                    </td>
                                                    <td className="p-3" style={{ whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'left' }}>{getItemDisplay(row)}</td>
                                                    <td className="p-3" style={{ textAlign: 'right' }}>{qty}</td>
                                                    <td className="p-3" style={{ textAlign: 'left' }}>{unit}</td>
                                                    <td className="text-nowrap p-3" style={{ textAlign: 'left' }}>{jobOrderNumber}</td>
                                                </tr>
                                            );
                                        })
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center p-3">No items available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    );
                })()}
            </div>

            {showPrintModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ backgroundColor: 'white', width: '90%', height: '90%', borderRadius: '8px', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ padding: '15px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0 }}>Print Preview</h5>
                            <div>
                                <button
                                    className="btn btn-primary btn-sm me-2"
                                    onClick={handlePdfPrint}
                                    disabled={isPrinting}
                                >
                                    {isPrinting ? (
                                        <><i className="fas fa-spinner fa-spin me-1"></i> Generating...</>
                                    ) : (
                                        <><i className="fas fa-print me-1"></i> Print</>
                                    )}
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={handleClosePrintModal}>
                                    <i className="fas fa-times me-1"></i>
                                    Close
                                </button>
                            </div>
                        </div>
                        <div style={{ height: 'calc(100% - 70px)', overflow: 'auto', padding: '20px', fontFamily: 'sans-serif', textTransform: 'uppercase' }} className="modal-print-content-deliverynote">
                            <style>{`
                                .modal-print-content-deliverynote,
                                .modal-print-content-deliverynote * {
                                    font-family: sans-serif !important;
                                    text-transform: uppercase;
                                    font-weight: normal !important;
                                }
                                .modal-print-content-deliverynote #container {
                                    width: 200mm !important;
                                    max-width: 200mm !important;
                                    margin: 0 auto !important;
                                    padding: 0 !important;
                                }
                                .modal-print-content-deliverynote #items {
                                    width: 100% !important;
                                }
                                .modal-print-content-deliverynote .delivery-note-items-table {
                                    table-layout: fixed !important;
                                    width: 100% !important;
                                }
                                .modal-print-content-deliverynote .delivery-note-items-table th,
                                .modal-print-content-deliverynote .delivery-note-items-table td {
                                    font-weight: normal !important;
                                    vertical-align: top;
                                }
                                .modal-print-content-deliverynote .delivery-note-items-table .col-no,
                                .modal-print-content-deliverynote .delivery-note-items-table .col-name,
                                .modal-print-content-deliverynote .delivery-note-items-table .col-description,
                                .modal-print-content-deliverynote .delivery-note-items-table .col-unit,
                                .modal-print-content-deliverynote .delivery-note-items-table .col-job {
                                    text-align: left !important;
                                }
                                .modal-print-content-deliverynote .delivery-note-items-table .col-qty {
                                    text-align: right !important;
                                    white-space: nowrap;
                                }
                                .modal-print-content-deliverynote .delivery-note-items-table .col-description {
                                    word-break: break-word;
                                    overflow-wrap: break-word;
                                }
                                .modal-print-content-deliverynote #client-info .client-address {
                                    display: block !important;
                                }
                                .modal-print-content-deliverynote #client-contacts > div,
                                .modal-print-content-deliverynote #client-info > div {
                                    justify-content: flex-start !important;
                                }
                            `}</style>
                            <div id="container" style={{ fontFamily: 'sans-serif', textTransform: 'uppercase' }}>
                                <div className="print-content" style={{ fontFamily: 'sans-serif', textTransform: 'uppercase' }}>
                                    <div className="clearfix"></div>
                                    <div className="title">
                                        <span>DELIVERY NOTE</span>
                                    </div>
                                    <section className="middle-box">
                                        <section id="client-info">
                                            <span className="client-name invoice_client_name">
                                                {getText(data.customer_name) || getText(data.customer?.label) || ''}
                                            </span>
                                            <div className="client-address">
                                                <div>BUILDING {getText(data.building) || '-'}, FLAT {getText(data.flat) || '-'}, ROAD {getText(data.road) || '-'}</div>
                                                <div>BLOCK {getText(data.block) || '-'}, AREA {getText(data.area) || '-'}</div>
                                                <div>KINGDOM OF BAHRAIN</div>
                                            </div>
                                            {(hasContactValue(phone) || hasContactValue(vatNumber)) && (
                                                <section id="client-contacts">
                                                    {hasContactValue(phone) && (
                                                        <div>
                                                            <span className="client_label">PHONE : </span>
                                                            <span className="client_value">{phone}</span>
                                                        </div>
                                                    )}
                                                    {hasContactValue(vatNumber) && (
                                                        <div>
                                                            <span className="client_label">VAT NO: </span>
                                                            <span className="client_value">{vatNumber}</span>
                                                        </div>
                                                    )}
                                                </section>
                                            )}
                                        </section>

                                        <section id="invoice-info">
                                            <div>
                                                <span className="invoice_label">DELIVERY NUMBER : </span>
                                                <span className="invoice_value">{getText(data.invoice_number) || getText(data.number) || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="invoice_label">DELIVERY DATE : </span>
                                                <span className="invoice_value">{formatDate(data.invoice_date || data.delivery_date)}</span>
                                            </div>
                                            <div>
                                                <span className="invoice_label">LPO NO:</span>
                                                <span className="invoice_value">{getText(data.lpo_number?.label) || getText(data.lpo_number) || '-'}</span>
                                            </div>
                                            {data.lpo_date ? (
                                                <div>
                                                    <span className="invoice_label">LPO DATE:</span>
                                                    <span className="invoice_value">{formatDate(data.lpo_date)}</span>
                                                </div>
                                            ) : null}
                                        </section>
                                    </section>
                                    <div className="clearfix"></div>
                                    <section id="items">
                                        {(() => {
                                            const groupedItems = getGroupedItems();
                                            return (
                                                <table cellSpacing="0" cellPadding="0" className="delivery-note-items-table">
                                                    <thead>
                                                        <tr>
                                                            <th className="col-no">NO.</th>
                                                            <th className="col-name">NAME &amp; STAFF NO.</th>
                                                            <th className="col-description">DESCRIPTION</th>
                                                            <th className="col-qty">QTY</th>
                                                            <th className="col-unit">UNIT</th>
                                                            <th className="col-job">JOB NO.</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {groupedItems && groupedItems.length > 0 ? (
                                                            groupedItems.flatMap((group, groupIndex) => (
                                                                (Array.isArray(group.items) ? group.items : []).map((row, rowIndex) => {
                                                                    const serial = group.serial_number || groupIndex + 1;
                                                                    const employeeName = getText(group.employee_name) || '-';
                                                                    const unit = getText(row.unit) || getText(row.item?.unit) || '-';
                                                                    const qty = formatQuantity(row.quantity || row.qty || 0);
                                                                    const jobOrderNumber = getText(group.job_order_number) || getText(row.job_order_number) || '-';

                                                                    return (
                                                                        <tr key={`${group.job_order_id || group.job_order_number || groupIndex}-${row.id || rowIndex}`} data-iterate="item">
                                                                            <td className="invoice_item_row_number col-no">
                                                                                {rowIndex === 0 ? serial : ''}
                                                                            </td>
                                                                            <td className="col-name">
                                                                                {rowIndex === 0 ? employeeName : ''}
                                                                            </td>
                                                                            <td className="col-description">
                                                                                <span className="invoice_item_description">{getItemDisplay(row)}</span>
                                                                            </td>
                                                                            <td className="col-qty">
                                                                                <span className="invoice_item_quantity">{qty}</span>
                                                                            </td>
                                                                            <td className="col-unit">
                                                                                <span className="invoice_item_unit">{unit}</span>
                                                                            </td>
                                                                            <td className="col-job">
                                                                                <span className="invoice_item_job_number">{jobOrderNumber}</span>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="6" style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>No items available</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            );
                                        })()}
                                    </section>
                                    <div className="clearfix"></div>
                                    <section id="signature-area">
                                        <div className="signature-row">
                                            <div className="signature-block">
                                                <div className="signature-line"></div>
                                                <span className="signature-label">AUTHORIZED BY</span>
                                            </div>
                                            <div className="signature-block signature-block-right">
                                                <div className="signature-line"></div>
                                                <span className="signature-label">RECEIVED BY</span>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeliveryNotePreview;
